import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Compass, HelpCircle, MapPin } from 'lucide-react'
import { OVERVIEW_TOUR_ID } from '../lib/tours'
import { useWalkthroughStore } from '../stores/walkthrough.store'
import { useAuthStore } from '@/shared/stores/auth.store'
import { locallyOptedOut, recordPageStatus } from '../utils/page-status-write'
import { isImpersonating } from '../utils/impersonation'
import { useCurrentPageTour } from '../hooks/use-tour-context'

/**
 * Visible "?" entry in the header: replay the overview or get the page guide.
 * (Support stays in the avatar menu.) Same portal + outside-click pattern as the
 * notification and avatar menus.
 */
export function HelpMenu() {
  const [open, setOpen] = useState(false)
  const [newTag, setNewTag] = useState(false)
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const startTour = useWalkthroughStore((s) => s.startTour)
  const beaconTourId = useWalkthroughStore((s) => s.beaconTourId)
  const setBeaconTourId = useWalkthroughStore((s) => s.setBeaconTourId)
  const user = useAuthStore((s) => s.user)
  const userId = user?.id ?? null
  const pageTour = useCurrentPageTour()
  // Same gates as the card: never while impersonating, never after the opt-out.
  const beacon =
    !!beaconTourId &&
    beaconTourId === pageTour?.id &&
    !!userId &&
    !isImpersonating(user) &&
    user?.onboarding?.page_invites === true &&
    !locallyOptedOut(userId)

  useEffect(() => {
    if (!open) return
    function measure() {
      const header = document.querySelector<HTMLElement>('.header')
      if (!header) return
      const rect = header.getBoundingClientRect()
      const gap = window.innerWidth - rect.right
      setAnchor({ top: rect.bottom + gap, right: gap })
    }
    function onDown(e: MouseEvent) {
      const t = e.target as Node
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [])
      if (!items.length || !menuRef.current?.contains(document.activeElement)) return
      if (e.key === 'Tab') {
        setOpen(false) // menus close on Tab; focus moves on naturally
        return
      }
      const at = items.indexOf(document.activeElement as HTMLButtonElement)
      let next = -1
      if (e.key === 'ArrowDown') next = (at + 1) % items.length
      else if (e.key === 'ArrowUp') next = (at - 1 + items.length) % items.length
      else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = items.length - 1
      if (next >= 0) {
        e.preventDefault()
        items[next].focus()
      }
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Move focus into the menu when it opens (keyboard users land on the first item).
  useEffect(() => {
    if (open && anchor) menuRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus()
  }, [open, anchor])

  // Close first, start next frame — the menu must not be measured as part of the page.
  const launch = (tourId: string) => {
    setOpen(false)
    requestAnimationFrame(() => startTour(tourId, 'help'))
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`header-icon-btn${beacon ? ' mc-help-beacon' : ''}`}
        aria-label={beacon ? 'Help and tours (a guide is available for this page)' : 'Help and tours'}
        aria-haspopup="menu"
        aria-expanded={open}
        data-tour="header.help"
        onClick={() => {
          // Opening the menu on a beacon page = the guide has been offered.
          // Keep the "New" tag for this opening even though the beacon goes out.
          if (!open) setNewTag(beacon)
          if (!open && beacon && pageTour && userId) {
            void recordPageStatus(userId, pageTour.id, 'offered', pageTour.version)
            setBeaconTourId(null)
          }
          setOpen((v) => !v)
        }}
      >
        <HelpCircle size={18} />
      </button>
      {open && anchor && createPortal(
        <div ref={menuRef} role="menu" aria-label="Help and tours" className="mc-help-menu" style={{ top: anchor.top, right: anchor.right }}>
          <button type="button" role="menuitem" className="mc-help-item" onClick={() => launch(OVERVIEW_TOUR_ID)}>
            <Compass size={13} aria-hidden="true" />
            Take a tour
          </button>
          <button
            type="button"
            role="menuitem"
            className="mc-help-item"
            onClick={() => pageTour && launch(pageTour.id)}
            disabled={!pageTour}
            aria-describedby={pageTour ? undefined : 'mc-help-no-guide'}
          >
            <MapPin size={13} aria-hidden="true" />
            <span>
              Guide me through this page
              {(beacon || newTag) && <span className="mc-help-new">New</span>}
              {!pageTour && (
                <span id="mc-help-no-guide" className="mc-help-reason">No guide for this page yet</span>
              )}
            </span>
          </button>
        </div>,
        document.body,
      )}
    </>
  )
}
